import React, { useState } from 'react';
import { Shield, X, Check } from 'lucide-react';

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  role: string;
}

interface UserRoleModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (role: string) => void;
  user: User;
}

const UserRoleModal: React.FC<UserRoleModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  user
}) => {
  const [selectedRole, setSelectedRole] = useState<string>(user.role);

  if (!isOpen) return null;

  const roles = [
    { 
      value: 'user', 
      label: 'User', 
      description: 'Regular user with access to basic features'
    },
    { 
      value: 'admin', 
      label: 'Admin', 
      description: 'Admin with access to management features'
    },
    { 
      value: 'superadmin', 
      label: 'Superadmin', 
      description: 'Unrestricted access to all system features'
    }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onConfirm(selectedRole);
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity" onClick={onClose}>
          <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
        </div>

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="text-gray-400 hover:text-gray-500"
              onClick={onClose}
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-blue-100 sm:mx-0 sm:h-10 sm:w-10">
                <Shield className="h-6 w-6 text-blue-600" />
              </div>
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <h3 className="text-lg leading-6 font-medium text-gray-900">
                  Change User Role
                </h3>
                <div className="mt-2">
                  <p className="text-sm text-gray-500">
                    Change the role for <span className="font-semibold">{user.first_name} {user.last_name}</span>. This will modify their access permissions across the platform.
                  </p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="mt-6">
              <div className="space-y-4">
                {roles.map((role) => (
                  <div 
                    key={role.value}
                    className={`relative rounded-lg border p-4 flex cursor-pointer ${
                      selectedRole === role.value 
                        ? 'border-orange-500 bg-orange-50' 
                        : 'border-gray-300 hover:border-gray-400'
                    }`}
                    onClick={() => setSelectedRole(role.value)}
                  >
                    <div className="flex items-center h-5">
                      <input
                        id={`role-${role.value}`}
                        name="role"
                        type="radio"
                        checked={selectedRole === role.value}
                        onChange={() => setSelectedRole(role.value)}
                        className="focus:ring-orange-500 h-4 w-4 text-orange-600 border-gray-300"
                      />
                    </div>
                    <div className="ml-3 flex-1">
                      <label htmlFor={`role-${role.value}`} className="font-medium text-gray-700">
                        {role.label}
                      </label>
                      <p className="text-sm text-gray-500">{role.description}</p>
                    </div>
                    {selectedRole === role.value && (
                      <div className="text-orange-600">
                        <Check className="h-5 w-5" />
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="mt-6 bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="submit"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-orange-600 text-base font-medium text-white hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Update Role
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserRoleModal;